import type { OfflineStorage } from './OfflineStorage';
import { offlineFileName } from './constants';

/**
 * OPFS-backed storage. Keys (origin-relative paths from `resourceKey`) are
 * reversibly encoded into a single flat filename inside one namespace
 * directory, so we never mkdir deep trees.
 *
 * The root directory handle is injectable so the adapter can be unit-tested
 * against an in-memory File System Access fake (jsdom has no real OPFS).
 */
export class OpfsStorage implements OfflineStorage {
  private constructor(private readonly dir: FileSystemDirectoryHandle) {}

  static async create(namespace: string, root?: FileSystemDirectoryHandle): Promise<OpfsStorage> {
    const base = root ?? (await navigator.storage.getDirectory());
    const dir = await base.getDirectoryHandle(namespace, { create: true });
    return new OpfsStorage(dir);
  }

  private fileName(key: string): string {
    return offlineFileName(key);
  }

  async put(key: string, body: ReadableStream<Uint8Array>): Promise<void> {
    const name = this.fileName(key);
    const handle = await this.dir.getFileHandle(name, { create: true });
    const writable = await handle.createWritable();
    try {
      await body.pipeTo(writable);
    } catch (e) {
      try {
        await this.dir.removeEntry(name);
      } catch {
        /* best effort */
      }
      throw e;
    }
  }

  async get(key: string): Promise<Blob | null> {
    try {
      const handle = await this.dir.getFileHandle(this.fileName(key));
      return await handle.getFile();
    } catch {
      return null;
    }
  }

  async has(key: string): Promise<boolean> {
    try {
      await this.dir.getFileHandle(this.fileName(key));
      return true;
    } catch {
      return false;
    }
  }

  async delete(key: string): Promise<void> {
    try {
      await this.dir.removeEntry(this.fileName(key));
    } catch {
      /* already absent */
    }
  }

  async usage(): Promise<{ used: number; quota: number | null }> {
    const est = (await navigator.storage?.estimate?.()) ?? {};
    return { used: est.usage ?? 0, quota: est.quota ?? null };
  }

  /** Test/maintenance helper: remove every entry in this namespace. */
  async clearAll(): Promise<void> {
    // @ts-expect-error entries() exists in browsers though not always typed
    for await (const [name] of this.dir.entries()) {
      await this.dir.removeEntry(name);
    }
  }
}
